import { Node, Edge, getSmoothStepPath, Position } from 'reactflow';
import type { SurveyQuestionTemplate, Survey } from '../types';
import {
  BASE_NODE_STYLE,
  START_NODE_STYLE,
  END_NODE_STYLE,
  CONDITIONAL_NODE_STYLE,
  BASE_X,
  CONDITIONAL_RIGHT_X,
  VERTICAL_SPACING,
  CONDITIONAL_OFFSET_Y,
  createStartNode,
  createEndNode,
} from '../constants';

// Helper function to calculate distance from a point to a line segment
export function pointToLineSegmentDistance(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number
): number {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;
  
  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;
  
  if (lenSq !== 0) {
    param = dot / lenSq;
  }
  
  let xx: number, yy: number;
  
  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }
  
  const dx = px - xx;
  const dy = py - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

export const extractNumericId = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isStartNode = (node: Node) => node.id === 'start';
export const isEndNode = (node: Node) => node.id === 'end';

export const ensureStartEndNodes = (nodes: Node[]) => {
  let next = [...nodes];
  // Remove any existing "done" nodes (type === 'output' with ID !== 'end')
  next = next.filter((node) => !(node.type === 'output' && node.id !== 'end'));
  
  if (!next.some(isStartNode)) {
    next = [createStartNode(), ...next];
  }
  if (!next.some(isEndNode)) {
    next = [...next, createEndNode()];
  }
  return next;
};

export const layoutLinearNodes = (nodes: Node[]) => {
  const ensured = ensureStartEndNodes(nodes);
  const startNode = ensured.find(isStartNode) ?? createStartNode();
  const endNode = ensured.find(isEndNode) ?? createEndNode();
  const middleNodes = ensured
    .filter((node) => !isStartNode(node) && !isEndNode(node))
    .sort((a, b) => extractNumericId(a.id) - extractNumericId(b.id));
  const ordered = [startNode, ...middleNodes, endNode];
  return ordered.map((node, index) => {
    const yPos = index * VERTICAL_SPACING;
    const baseStyle =
      node.id === 'start' ? START_NODE_STYLE : node.id === 'end' ? END_NODE_STYLE : node.style || BASE_NODE_STYLE;
    return {
      ...node,
      position: { x: node.position?.x ?? BASE_X, y: yPos },
      style: baseStyle,
    };
  });
};

export const buildLinearEdgesFromNodes = (nodes: Node[]): Edge[] => {
  const sequence = nodes.map((node) => node.id);
  const edges: Edge[] = [];
  for (let i = 0; i < sequence.length - 1; i += 1) {
    const source = sequence[i];
    const target = sequence[i + 1];
    edges.push({
      id: `edge-${source}-${target}`,
      source,
      target,
      type: 'smoothstep',
      data: { linear: true },
      reconnectable: true,
    });
  }
  return edges;
};

export const mergeLinearEdges = (existing: Edge[] = [], linear: Edge[]) => {
  const nonLinear = existing.filter((edge) => !edge.data?.linear);
  return [...linear, ...nonLinear];
};

export const findConnectedPathFromStartToEnd = (nodes: Node[], edges: Edge[]) => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const startNode = nodes.find((n) => n.id === 'start');
  const endNode = nodes.find((n) => n.id === 'end');

  if (!startNode || !endNode) {
    return { connectedNodes: [], connectedEdges: [] };
  }

  const adjacencyList = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, []);
    }
    adjacencyList.get(edge.source)!.push(edge.target);
  });

  const reachableFromStart = new Set<string>();
  const dfsForward = (nodeId: string) => {
    if (reachableFromStart.has(nodeId)) {
      return;
    }
    reachableFromStart.add(nodeId);
    const neighbors = adjacencyList.get(nodeId) || [];
    neighbors.forEach((neighbor) => {
      dfsForward(neighbor);
    });
  };
  dfsForward('start');

  if (!reachableFromStart.has('end')) {
    return { connectedNodes: [], connectedEdges: [] };
  }

  const canReachEnd = new Set<string>();
  const reverseAdjacencyList = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (!reverseAdjacencyList.has(edge.target)) {
      reverseAdjacencyList.set(edge.target, []);
    }
    reverseAdjacencyList.get(edge.target)!.push(edge.source);
  });

  const dfsBackward = (nodeId: string) => {
    if (canReachEnd.has(nodeId)) {
      return;
    }
    canReachEnd.add(nodeId);
    const neighbors = reverseAdjacencyList.get(nodeId) || [];
    neighbors.forEach((neighbor) => {
      if (reachableFromStart.has(neighbor)) {
        dfsBackward(neighbor);
      }
    });
  };
  dfsBackward('end');

  const connectedNodeIds = new Set<string>();
  reachableFromStart.forEach((nodeId) => {
    if (canReachEnd.has(nodeId)) {
      connectedNodeIds.add(nodeId);
    }
  });

  const connectedNodes = Array.from(connectedNodeIds)
    .map((id) => nodeMap.get(id))
    .filter((n): n is Node => n !== undefined);

  const connectedEdges = edges.filter(
    (edge) => connectedNodeIds.has(edge.source) && connectedNodeIds.has(edge.target)
  );

  return { connectedNodes, connectedEdges };
};

export const extractOptionsFromLabel = (label?: string) => {
  if (!label) {
    return [];
  }
  const optionLine = label
    .split('\n')
    .find((line: string) => line.trim().toLowerCase().startsWith('options:') || line.trim().toLowerCase().startsWith('choices:'));
  if (!optionLine) {
    return [];
  }
  const [, list] = optionLine.split(':');
  if (!list) {
    return [];
  }
  return list
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

// Parse question text from node label (removes Q number prefix and options/scale lines)
export const parseQuestionText = (label?: string): string => {
  if (!label) return '';
  const lines = label.split('\n');
  // First line typically has "Q{number}: {question text}"
  const firstLine = lines[0] || '';
  const match = firstLine.match(/^Q\d+(?:\.\d+)?:\s*(.+)$/);
  if (match) {
    return match[1].trim();
  }
  // Fallback: return first line without Q prefix
  return firstLine.replace(/^Q\d+(?:\.\d+)?:\s*/, '').trim();
};

// Parse rating config from node label
export const parseRatingConfig = (label?: string | null): { format: 'numerical' | 'satisfaction'; rate_amount: number } | null => {
  if (!label) return null;
  const scaleLine = label.split('\n').find((line: string) => line.trim().toLowerCase().startsWith('scale:'));
  if (!scaleLine) return null;
  
  // Match: "Scale: numerical (5)" or "Scale: satisfaction (5)"
  const match = scaleLine.match(/scale:\s*(\w+)\s*\((\d+)\)/i);
  if (match && match[1] && match[2]) {
    const format = match[1].toLowerCase() === 'satisfaction' ? 'satisfaction' : 'numerical';
    const rate_amount = parseInt(match[2], 10) || 5;
    return { format, rate_amount };
  }
  return null;
};

// Determine question type from node label and metadata
export const determineQuestionType = (node: Node): 'rating' | 'radio' | 'checkbox' | 'textfield' | 'done' => {
  // Check metadata first
  const meta = node.data?.meta as SurveyQuestionTemplate | undefined;
  if (meta?.type) {
    return meta.type;
  }
  
  // Check node type
  if (node.type === 'output' || node.id === 'end') {
    return 'done';
  }
  
  // Parse from label
  const label = node.data?.label || '';
  if (label.toLowerCase().includes('done') || label.toLowerCase().trim() === 'done') {
    return 'done';
  }
  
  // Check for rating scale
  if (label.toLowerCase().includes('scale:')) {
    return 'rating';
  }
  
  // Check for options/choices
  if (label.toLowerCase().includes('options:') || label.toLowerCase().includes('choices:')) {
    const optionLine = label.split('\n').find((line: string) => 
      line.trim().toLowerCase().startsWith('options:') || line.trim().toLowerCase().startsWith('choices:')
    );
    if (optionLine?.toLowerCase().startsWith('choices:')) {
      return 'checkbox';
    }
    return 'radio';
  }
  
  // Default to textfield if it has question text but no special config
  if (parseQuestionText(label)) {
    return 'textfield';
  }
  
  return 'textfield';
};

// Check if edge is conditional
export const isConditionalEdge = (edge?: Edge | null) => Boolean(edge?.data?.conditionalOption && edge?.data?.conditionalOption !== 'else');

// Convert React Flow nodes/edges to survey JSON format
export const convertFlowToSurveyFormat = (nodes: Node[], edges: Edge[]): any[] => {
  const survey: any[] = [];
  const processedNodes = new Set<string>();
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  
  // Build edge maps
  const outgoingEdges = new Map<string, Edge[]>();
  edges.forEach((edge) => {
    if (!outgoingEdges.has(edge.source)) {
      outgoingEdges.set(edge.source, []);
    }
    outgoingEdges.get(edge.source)!.push(edge);
  });
  
  // Find the main path from start to end (following non-conditional edges)
  const findMainPath = (): string[] => {
    const path: string[] = ['start'];
    const visited = new Set<string>(['start']);
    let current = 'start';
    
    while (current !== 'end') {
      const edges = outgoingEdges.get(current) || [];
      // Prefer non-conditional edges for main path
      const nonConditional = edges.filter((e) => !isConditionalEdge(e));
      const nextEdge = nonConditional.length > 0 ? nonConditional[0] : edges[0];
      
      if (!nextEdge || visited.has(nextEdge.target)) {
        break;
      }
      
      current = nextEdge.target;
      path.push(current);
      visited.add(current);
    }
    
    return path;
  };
  
  const mainPath = findMainPath();
  if (import.meta.env.DEV) {
    console.log('🔍 convertFlowToSurveyFormat - mainPath:', mainPath);
    console.log('🔍 convertFlowToSurveyFormat - nodes:', nodes.map(n => ({ id: n.id, type: n.type, label: n.data?.label })));
    console.log('🔍 convertFlowToSurveyFormat - edges:', edges.map(e => ({ source: e.source, target: e.target, isConditional: isConditionalEdge(e) })));
  }
  let questionNumber = 1;
  
  // Get all question nodes (excluding start, end, and sub-questions for now)
  const allQuestionNodes = nodes.filter(n => {
    if (n.id === 'start' || n.id === 'end') return false;
    if (n.type !== 'question') return false;
    // Exclude sub-questions (they'll be processed separately)
    if (n.id.includes('.') && n.id.match(/^\d+\.\d+$/)) return false;
    return true;
  });
  
  // Build a processing order: main path first, then other question nodes
  const nodesToProcess: string[] = [];
  
  // First, add nodes from main path (in order)
  for (let i = 1; i < mainPath.length - 1; i++) { // Skip 'start' and 'end'
    const nodeId = mainPath[i];
    if (!nodesToProcess.includes(nodeId)) {
      nodesToProcess.push(nodeId);
    }
  }
  
  // Then, add any other question nodes not in main path (connected to Start via non-conditional edges)
  const startEdges = outgoingEdges.get('start') || [];
  const startNonConditionalTargets = startEdges
    .filter(e => !isConditionalEdge(e))
    .map(e => e.target)
    .filter(id => id !== 'start' && id !== 'end');
  
  startNonConditionalTargets.forEach(nodeId => {
    if (!nodesToProcess.includes(nodeId)) {
      const node = nodeMap.get(nodeId);
      if (node && node.type === 'question' && !node.id.includes('.')) {
        nodesToProcess.push(nodeId);
      }
    }
  });
  
  // Finally, add any remaining question nodes
  allQuestionNodes.forEach(node => {
    if (!nodesToProcess.includes(node.id)) {
      nodesToProcess.push(node.id);
    }
  });

  if (import.meta.env.DEV) {
    console.log('🔍 Nodes to process:', nodesToProcess);
  }

  // Process each node in the order we determined
  for (const nodeId of nodesToProcess) {
    if (processedNodes.has(nodeId)) continue;
    processedNodes.add(nodeId);
    
    const node = nodeMap.get(nodeId);
    if (!node) continue;
    
    const questionType = determineQuestionType(node);
    const label = node.data?.label || '';
    
    // Skip done questions in the middle of the path (they should only be at the end)
    // This prevents "floating done" issues
    if (questionType === 'done') {
      continue;
    }
    
    // Build question object
    // Extract question data from node.data.meta (preferred) or node.data
    const nodeMeta = node.data?.meta || {};
    const nodeData = node.data || {};
    
    // Extract question number from label (Q{number}: format) or use sequential numbering
    // IMPORTANT: Only use extracted number if it's an integer (main question), not a decimal (sub-question)
    // Also ensure the node ID doesn't contain a decimal (sub-questions have decimal IDs)
    let questionNum = questionNumber;
    const labelMatch = label.match(/^Q(\d+(?:\.\d+)?):/);
    if (labelMatch && !nodeId.includes('.')) {
      // Only process if node ID doesn't contain a decimal (ensures it's a main question)
      const extractedNum = parseFloat(labelMatch[1]);
      // Only use if it's an integer (not a decimal like 7.1)
      if (!isNaN(extractedNum) && extractedNum.toString() === Math.floor(extractedNum).toString()) {
        questionNum = Math.floor(extractedNum);
      }
    }
    
    // Use sequential numbering if no number found in label or if it was a decimal
    if (questionNum === questionNumber) {
      questionNumber++;
    }
    
    const question: any = {
      number: questionNum,
      type: questionType,
    };
    
    // Add question text - REQUIRED for all question types except 'done'
    // Priority: node.data.meta.question > node.data.question > parse from label > fallback
    let questionText = nodeMeta.question || 
                      nodeData.question || 
                      parseQuestionText(label) ||
                      '';
    
    // If still no question text, try extracting from label directly
    if (!questionText) {
      const fallbackText = label.split('\n')[0]?.replace(/^Q\d+(?:\.\d+)?:\s*/, '').trim() || '';
      if (fallbackText && !fallbackText.toLowerCase().includes('done')) {
        questionText = fallbackText;
      }
    }
    
    // Ensure question property exists (required for all types except 'done')
    if (questionText) {
      question.question = questionText;
    } else if (question.type !== 'done') {
      // Last resort: use a default question text to ensure the property exists
      question.question = `Question ${question.number}`;
    }
    
    // Add type_config - Priority: node.data.meta.type_config > parse from label
    if (nodeMeta.type_config) {
      question.type_config = nodeMeta.type_config;
    } else if (questionType === 'rating') {
      const ratingConfig = parseRatingConfig(label);
      if (ratingConfig) {
        // Ensure satisfaction format always has rate_amount of 5
        if (ratingConfig.format === 'satisfaction') {
          ratingConfig.rate_amount = 5;
        }
        question.type_config = ratingConfig;
      } else {
        question.type_config = { format: 'numerical', rate_amount: 5 };
      }
    } else if (questionType === 'radio') {
      const options = extractOptionsFromLabel(label);
      if (options.length > 0) {
        question.type_config = { radio_options: options };
      } else if (nodeMeta.type_config?.radio_options) {
        question.type_config = { radio_options: nodeMeta.type_config.radio_options };
      }
    } else if (questionType === 'checkbox') {
      const options = extractOptionsFromLabel(label);
      if (options.length > 0) {
        question.type_config = { checkbox_options: options };
      } else if (nodeMeta.type_config?.checkbox_options) {
        question.type_config = { checkbox_options: nodeMeta.type_config.checkbox_options };
      }
    }
    
    // Add conditional properties - Priority: node.data.meta > extract from edges
    if (nodeMeta.conditional !== undefined) {
      question.conditional = nodeMeta.conditional;
    }
    
    if (nodeMeta.conditional_config) {
      // Convert to array format if it's an object (backward compatibility)
      if (Array.isArray(nodeMeta.conditional_config)) {
        question.conditional_config = nodeMeta.conditional_config;
      } else {
        // Single object -> convert to array
        question.conditional_config = [nodeMeta.conditional_config];
      }
    }
    
    // Check edges to determine if conditional (if not already set from meta)
    if (question.conditional === undefined) {
      const conditionalEdges = (outgoingEdges.get(nodeId) || []).filter(isConditionalEdge);
      question.conditional = conditionalEdges.length > 0;
      if (conditionalEdges.length > 0 && import.meta.env.DEV) {
        console.log(`✅ Question ${question.number} detected as conditional from ${conditionalEdges.length} conditional edge(s)`);
      }
    }
    
    // Ensure conditional is always a boolean
    if (question.conditional === undefined) {
      question.conditional = false;
    }
    
    // Find the next question after this question (following non-conditional edges)
    // May need to traverse through sub-questions to find the actual next main question
    // This helps the system know the flow path
    // We calculate this BEFORE processing conditional_config so we can use it as a fallback
    let nextQuestionNumber: number | null = null;
    const findNextMainQuestion = (fromNodeId: string, visited: Set<string> = new Set()): Node | null => {
      if (visited.has(fromNodeId)) return null; // Prevent cycles
      visited.add(fromNodeId);
      
      const outgoingEdgesFromNode = outgoingEdges.get(fromNodeId) || [];
      const nextEdge = outgoingEdgesFromNode.find(e => !isConditionalEdge(e));
      
      if (!nextEdge) return null;
      
      const nextNode = nodeMap.get(nextEdge.target);
      if (!nextNode || nextNode.id === 'end') return null;
      
      // If it's a sub-question, traverse through it
      if (nextNode.id.includes('.') && nextNode.id.match(/^\d+\.\d+$/)) {
        return findNextMainQuestion(nextNode.id, visited);
      }
      
      // If it's a main question, return it
      if (!nextNode.id.includes('.')) {
        return nextNode;
      }
      
      return null;
    };
    
    const nextMainQuestionNode = findNextMainQuestion(nodeId);
    if (nextMainQuestionNode) {
      // Extract question number from next node's label or ID
      const nextLabel = nextMainQuestionNode.data?.label || '';
      const nextLabelMatch = nextLabel.match(/^Q(\d+(?:\.\d+)?):/);
      if (nextLabelMatch) {
        const extractedNum = parseFloat(nextLabelMatch[1]);
        if (!isNaN(extractedNum) && !extractedNum.toString().includes('.')) {
          nextQuestionNumber = extractedNum;
        }
      } else if (nextMainQuestionNode.id && !nextMainQuestionNode.id.includes('.')) {
        // Try to parse from node ID
        const nextIdNum = parseFloat(nextMainQuestionNode.id);
        if (!isNaN(nextIdNum)) {
          nextQuestionNumber = nextIdNum;
        }
      }
    }
    
    // Add next_question if found (null/undefined means it goes to Done)
    if (nextQuestionNumber !== null) {
      question.next_question = nextQuestionNumber;
    }
    
    // Extract all sub-questions into conditional_config as an array
    // This keeps all sub-questions grouped under the parent question
    // We do this AFTER calculating the parent's next_question so we can use it as a fallback
    if (question.conditional && !question.conditional_config) {
      const conditionalEdges = (outgoingEdges.get(nodeId) || []).filter(isConditionalEdge);
      if (conditionalEdges.length > 0) {
        // Sort conditional edges by target node ID to ensure consistent ordering
        conditionalEdges.sort((a, b) => {
          const aMatch = a.target.match(/^(\d+)\.(\d+)$/);
          const bMatch = b.target.match(/^(\d+)\.(\d+)$/);
          if (aMatch && bMatch) {
            const aParent = parseInt(aMatch[1], 10);
            const bParent = parseInt(bMatch[1], 10);
            const aSub = parseInt(aMatch[2], 10);
            const bSub = parseInt(bMatch[2], 10);
            if (aParent !== bParent) return aParent - bParent;
            return aSub - bSub;
          }
          return a.target.localeCompare(b.target);
        });
        
        // Build array of all conditional configs
        const conditionalConfigs: any[] = [];
        
        conditionalEdges.forEach((conditionalEdge) => {
          const conditionalNode = nodeMap.get(conditionalEdge.target);
          if (conditionalNode) {
            const conditionalMeta = conditionalNode.data?.meta || {};
            const conditionalNodeId = conditionalNode.id;
            const conditionalNumberMatch = conditionalNodeId.match(/^(\d+)\.(\d+)$/);
            let conditionalNumber = parseFloat(`${question.number}.1`);
            if (conditionalNumberMatch) {
              const parentNum = parseInt(conditionalNumberMatch[1], 10);
              const subNum = parseInt(conditionalNumberMatch[2], 10);
              if (parentNum === question.number) {
                conditionalNumber = parseFloat(`${parentNum}.${subNum}`);
              }
            }
            
            const conditionalQuestionText = conditionalMeta.question || 
              conditionalNode.data?.question ||
              parseQuestionText(conditionalNode.data?.label || '') ||
              conditionalNode.data?.label?.split('\n')[0]?.replace(/^Q\d+(?:\.\d+)?:\s*/, '').trim() || 
              'Please provide additional information';
            
            const conditionalType = determineQuestionType(conditionalNode);
            const condition = conditionalEdge.data?.conditionalOption || 
                             conditionalEdge.label || 
                             '';
            
            // Find the next question after this sub-question (following non-conditional edges)
            // May need to traverse through other sub-questions to find the actual next main question
            let conditionalNextQuestionNumber: number | null = null;
            const nextMainQuestionNodeFromSub = findNextMainQuestion(conditionalEdge.target);
            if (nextMainQuestionNodeFromSub) {
              // Extract question number from next node's label or ID
              const nextLabel = nextMainQuestionNodeFromSub.data?.label || '';
              const nextLabelMatch = nextLabel.match(/^Q(\d+(?:\.\d+)?):/);
              if (nextLabelMatch) {
                const extractedNum = parseFloat(nextLabelMatch[1]);
                if (!isNaN(extractedNum) && !extractedNum.toString().includes('.')) {
                  conditionalNextQuestionNumber = extractedNum;
                }
              } else if (nextMainQuestionNodeFromSub.id && !nextMainQuestionNodeFromSub.id.includes('.')) {
                // Try to parse from node ID
                const nextIdNum = parseFloat(nextMainQuestionNodeFromSub.id);
                if (!isNaN(nextIdNum)) {
                  conditionalNextQuestionNumber = nextIdNum;
                }
              }
            }
            
            // If we couldn't find a next question from the sub-question's edges,
            // use the parent question's next_question as a fallback
            // This ensures consistency, especially for old surveys that might not have explicit edges
            if (conditionalNextQuestionNumber === null && nextQuestionNumber !== null) {
              conditionalNextQuestionNumber = nextQuestionNumber;
            }
            
            const conditionalConfig: any = {
              condition: condition,
              number: conditionalNumber,
              response_question: conditionalQuestionText,
              type: conditionalType === 'textfield' ? 'textfield' : conditionalType,
            };
            
            // Add type_config for conditional questions (similar to main questions)
            const conditionalLabel = conditionalNode.data?.label || '';
            if (conditionalMeta.type_config) {
              conditionalConfig.type_config = conditionalMeta.type_config;
            } else if (conditionalType === 'rating') {
              const ratingConfig = parseRatingConfig(conditionalLabel);
              if (ratingConfig) {
                // Ensure satisfaction format always has rate_amount of 5
                if (ratingConfig.format === 'satisfaction') {
                  ratingConfig.rate_amount = 5;
                }
                conditionalConfig.type_config = ratingConfig;
              } else {
                conditionalConfig.type_config = { format: 'numerical', rate_amount: 5 };
              }
            } else if (conditionalType === 'radio') {
              const options = extractOptionsFromLabel(conditionalLabel);
              if (options.length > 0) {
                conditionalConfig.type_config = { radio_options: options };
              } else if (conditionalMeta.type_config?.radio_options) {
                conditionalConfig.type_config = { radio_options: conditionalMeta.type_config.radio_options };
              }
            } else if (conditionalType === 'checkbox') {
              const options = extractOptionsFromLabel(conditionalLabel);
              if (options.length > 0) {
                conditionalConfig.type_config = { checkbox_options: options };
              } else if (conditionalMeta.type_config?.checkbox_options) {
                conditionalConfig.type_config = { checkbox_options: conditionalMeta.type_config.checkbox_options };
              }
            }
            
            // Add next_question if found (either from sub-question's edges or parent's fallback)
            if (conditionalNextQuestionNumber !== null) {
              conditionalConfig.next_question = conditionalNextQuestionNumber;
            }
            
            conditionalConfigs.push(conditionalConfig);
            
            processedNodes.add(conditionalEdge.target);
          }
        });

        // Set conditional_config as an array containing all sub-questions
        question.conditional_config = conditionalConfigs;
        if (import.meta.env.DEV) {
          console.log(`✅ Extracted ${conditionalConfigs.length} conditional_config(s) for Q${question.number}:`, conditionalConfigs);
        }
      }
    }
    
    survey.push(question);
  }
  
  // Add done question at the end if not already present
  const hasDone = survey.some((q) => q.type === 'done');
  if (!hasDone) {
    survey.push({
      number: questionNumber,
      type: 'done',
    });
  }
  
  return survey;
};

export const getNodeOptions = (node?: Node) => {
  if (!node) {
    return [];
  }
  const meta = node.data?.meta as SurveyQuestionTemplate | undefined;
  if (meta?.type_config?.radio_options?.length) {
    return meta.type_config.radio_options;
  }
  if (meta?.type_config?.checkbox_options?.length) {
    return meta.type_config.checkbox_options;
  }
  if (typeof node.data?.options !== 'undefined' && Array.isArray(node.data.options)) {
    return node.data.options as string[];
  }
  return extractOptionsFromLabel(typeof node.data?.label === 'string' ? node.data.label : undefined);
};

export const removeOptionFromLabelText = (label: string, option: string) => {
  if (!label) {
    return label;
  }
  const lines = label.split('\n');
  const optionLineIndex = lines.findIndex((line) => {
    const trimmed = line.trim().toLowerCase();
    return trimmed.startsWith('options:') || trimmed.startsWith('choices:');
  });
  if (optionLineIndex === -1) {
    return label;
  }

  const [prefix, list = ''] = lines[optionLineIndex].split(':');
  const options = list
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((opt) => opt !== option);

  if (options.length === 0) {
    lines.splice(optionLineIndex, 1);
  } else {
    lines[optionLineIndex] = `${prefix.trim()}: ${options.join(', ')}`;
  }

  return lines.filter((line, index) => line.trim() !== '' || index === 0).join('\n').trim();
};

// Reconnect edges after a node deletion, handling radio/checkbox questions intelligently
export const reconnectAfterDeletion = (
  deletedNodeId: string,
  _deletedNode: Node | undefined, // Currently unused, but kept for future enhancements
  nodes: Node[],
  edges: Edge[]
): Edge[] => {
  const updatedEdges = [...edges];
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  
  // Check if deleted node is a sub-question (has decimal ID like "1.1", "2.1")
  const isSubQuestion = deletedNodeId.includes('.') && deletedNodeId.match(/^\d+\.\d+$/);
  
  // Find all edges connected to the deleted node
  const incomingEdges = edges.filter(e => e.target === deletedNodeId);
  const outgoingEdges = edges.filter(e => e.source === deletedNodeId);
  
  // If no connections, nothing to reconnect
  if (incomingEdges.length === 0 && outgoingEdges.length === 0) {
    return updatedEdges;
  }
  
  // For sub-questions: identify children that should be orphaned (no other paths to them)
  const orphanedChildren = new Set<string>();
  if (isSubQuestion && outgoingEdges.length > 0) {
    outgoingEdges.forEach(outgoingEdge => {
      const childNodeId = outgoingEdge.target;
      // Check if there are other incoming edges to this child (from other sub-questions or main path)
      const otherIncomingToChild = edges.filter(e => 
        e.target === childNodeId && 
        e.source !== deletedNodeId &&
        e.source !== 'start'
      );
      
      // If there are no other paths to the child, mark it as orphaned
      if (otherIncomingToChild.length === 0) {
        orphanedChildren.add(childNodeId);
        if (import.meta.env.DEV) {
          console.log(`⚠️ Sub-question ${deletedNodeId} was the only path to ${childNodeId}, leaving ${childNodeId} orphaned`);
        }
      }
    });
  }
  
  // For each incoming edge, reconnect to the deleted node's outgoing targets
  incomingEdges.forEach(incomingEdge => {
    const sourceId = incomingEdge.source;
    
    // CRITICAL: If the deleted node is a sub-question and the incoming edge is conditional,
    // we should NOT reconnect it. The conditional edge should just be removed.
    // Other conditional edges to remaining sub-questions should remain intact.
    if (isSubQuestion && isConditionalEdge(incomingEdge)) {
      // For sub-questions with conditional incoming edges, just remove the edge
      // Don't try to reconnect - the conditional edge is specific to this sub-question
      const edgeIndex = updatedEdges.findIndex(e => e.id === incomingEdge.id);
      if (edgeIndex >= 0) {
        updatedEdges.splice(edgeIndex, 1);
      }
      return; // Skip reconnection for conditional edges to sub-questions
    }
    
    // Find the best target to reconnect to from the deleted node's outgoing edges
    // Prefer non-conditional edges, and skip radio/checkbox questions if possible
    // Also skip orphaned children (they should remain orphaned)
    const nonConditionalOutgoing = outgoingEdges.filter(e => 
      !isConditionalEdge(e) && !orphanedChildren.has(e.target)
    );
    const conditionalOutgoing = outgoingEdges.filter(e => 
      isConditionalEdge(e) && !orphanedChildren.has(e.target)
    );
    
    // Try to find a target that's not a radio/checkbox question
    let bestTarget: string | null = null;
    let bestEdge: Edge | null = null;
    
    // First, try non-conditional edges to non-radio/checkbox questions
    for (const edge of nonConditionalOutgoing) {
      const targetNode = nodeMap.get(edge.target);
      if (targetNode && targetNode.id !== 'start' && targetNode.id !== 'end') {
        const questionType = determineQuestionType(targetNode);
        if (questionType !== 'radio' && questionType !== 'checkbox') {
          bestTarget = edge.target;
          bestEdge = edge;
          break;
        }
      } else if (targetNode && targetNode.id === 'end') {
        // End is always a good target
        bestTarget = 'end';
        bestEdge = edge;
        break;
      }
    }
    
    // If no good target found, try any non-conditional edge
    if (!bestTarget && nonConditionalOutgoing.length > 0) {
      bestEdge = nonConditionalOutgoing[0];
      bestTarget = bestEdge.target;
    }
    
    // If still no target, try conditional edges (but skip radio/checkbox)
    if (!bestTarget) {
      for (const edge of conditionalOutgoing) {
        const targetNode = nodeMap.get(edge.target);
        if (targetNode && targetNode.id !== 'start' && targetNode.id !== 'end') {
          const questionType = determineQuestionType(targetNode);
          if (questionType !== 'radio' && questionType !== 'checkbox') {
            bestTarget = edge.target;
            bestEdge = edge;
            break;
          }
        }
      }
    }
    
    // If we found a target, create a new edge
    if (bestTarget && bestEdge) {
      // Check if edge already exists
      const existingEdge = updatedEdges.find(
        e => e.source === sourceId && e.target === bestTarget
      );
      
      if (!existingEdge) {
        updatedEdges.push({
          id: `edge-${sourceId}-${bestTarget}`,
          source: sourceId,
          target: bestTarget,
          type: bestEdge.type || 'smoothstep',
          data: { ...bestEdge.data, reconnected: true },
          reconnectable: true,
          sourceHandle: incomingEdge.sourceHandle || 'bottom-output',
          targetHandle: bestEdge.targetHandle || 'top-input',
        });
      }
    } else if (outgoingEdges.length > 0 && !isSubQuestion) {
      // No good target found, but we have outgoing edges - connect to the first one
      // This handles the case where all targets are radio/checkbox (we can orphan them)
      // BUT: Skip this for sub-questions if all children are orphaned
      const firstOutgoing = outgoingEdges.find(e => !orphanedChildren.has(e.target)) || outgoingEdges[0];
      if (firstOutgoing && !orphanedChildren.has(firstOutgoing.target)) {
        const existingEdge = updatedEdges.find(
          e => e.source === sourceId && e.target === firstOutgoing.target
        );
        
        if (!existingEdge) {
          updatedEdges.push({
            id: `edge-${sourceId}-${firstOutgoing.target}`,
            source: sourceId,
            target: firstOutgoing.target,
            type: firstOutgoing.type || 'smoothstep',
            data: { ...firstOutgoing.data, reconnected: true },
            reconnectable: true,
            sourceHandle: incomingEdge.sourceHandle || 'bottom-output',
            targetHandle: firstOutgoing.targetHandle || 'top-input',
          });
        }
      }
    } else if (!isSubQuestion || orphanedChildren.size === 0) {
      // No outgoing edges from deleted node - connect directly to end
      // BUT: Skip this for sub-questions if children are orphaned
      const existingEdge = updatedEdges.find(
        e => e.source === sourceId && e.target === 'end'
      );
      
      if (!existingEdge) {
        updatedEdges.push({
          id: `edge-${sourceId}-end`,
          source: sourceId,
          target: 'end',
          type: 'smoothstep',
          data: { linear: true, reconnected: true },
          reconnectable: true,
          sourceHandle: incomingEdge.sourceHandle || 'bottom-output',
          targetHandle: 'top-input',
        });
      }
    }
  });
  
  // For sub-questions: also remove all outgoing edges from the deleted sub-question
  // These edges should not be reconnected - they're specific to the deleted sub-question
  if (isSubQuestion && outgoingEdges.length > 0) {
    outgoingEdges.forEach(outgoingEdge => {
      const edgeIndex = updatedEdges.findIndex(e => e.id === outgoingEdge.id);
      if (edgeIndex >= 0) {
        updatedEdges.splice(edgeIndex, 1);
      }
    });
  }
  
  return updatedEdges;
};

// Ensure there's always a path from start to done by connecting disconnected questions
// Helper to check if ALL options of a question have conditional edges
export const allOptionsAreConditional = (nodeId: string, nodes: Node[], edges: Edge[]): boolean => {
  const node = nodes.find(n => n.id === nodeId);
  if (!node) return false;
  
  // Get all options from the question
  const allOptions = getNodeOptions(node);
  if (allOptions.length === 0) return false; // No options means not all conditional
  
  // Find which options have conditional edges (sub-questions)
  const conditionalOptions = new Set(
    edges
      .filter(e => e.source === nodeId && isConditionalEdge(e))
      .map(e => e.data?.conditionalOption as string)
      .filter(Boolean)
  );
  
  // Check if ALL options have conditional edges
  return allOptions.length > 0 && allOptions.every(opt => conditionalOptions.has(opt));
};

// Helper to rebuild edge label from question node's non-conditional options
export const rebuildEdgeLabelFromQuestion = (nodeId: string, nodes: Node[], edges: Edge[]): string | null => {
  const node = nodes.find(n => n.id === nodeId);
  if (!node) return null;
  
  // Get all options from the question
  const allOptions = getNodeOptions(node);
  if (allOptions.length === 0) return null;
  
  // Find which options have conditional edges (sub-questions)
  const conditionalOptions = new Set(
    edges
      .filter(e => e.source === nodeId && isConditionalEdge(e))
      .map(e => e.data?.conditionalOption as string)
      .filter(Boolean)
  );
  
  // Non-conditional options should be in the label
  const nonConditionalOptions = allOptions.filter(opt => !conditionalOptions.has(opt));
  
  return nonConditionalOptions.length > 0 ? nonConditionalOptions.join(', ') : null;
};

export const ensureStartToDoneConnection = (
  nodes: Node[], 
  edges: Edge[],
  removedEdgeLabels?: Map<string, string>
): Edge[] => {
  const questionNodes = nodes.filter(n => n.id !== 'start' && n.id !== 'end' && n.type === 'question');
  
  if (questionNodes.length === 0) {
    // No questions, just connect start to end
    const hasStartToEnd = edges.some(e => e.source === 'start' && e.target === 'end');
    if (!hasStartToEnd) {
      return [...edges, {
        id: 'edge-start-end',
        source: 'start',
        target: 'end',
        type: 'smoothstep',
        data: { linear: true },
        reconnectable: true,
        sourceHandle: 'bottom-output',
        targetHandle: 'top-input',
      }];
    }
    return edges;
  }
  
  // Build adjacency list
  const adjacencyList = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, []);
    }
    adjacencyList.get(edge.source)!.push(edge.target);
  });
  
  // Check if start can reach end
  const canReachEnd = (from: string, visited: Set<string> = new Set()): boolean => {
    if (from === 'end') return true;
    if (visited.has(from)) return false;
    visited.add(from);
    
    const neighbors = adjacencyList.get(from) || [];
    return neighbors.some(neighbor => canReachEnd(neighbor, new Set(visited)));
  };
  
  let updatedEdges = [...edges];
  
  // Check if start can reach end through the current edges
  const startCanReachEnd = canReachEnd('start');
  
  // If start CAN reach end, remove any direct Start->Done edge (auto-complete line)
  // This ensures that when user manually connects Start to a question, the auto-complete line is removed
  if (startCanReachEnd && questionNodes.length > 0) {
    // Remove the direct Start->Done edge if it exists (it's no longer needed)
    const beforeCount = updatedEdges.filter(e => e.source === 'start' && e.target === 'end').length;
    updatedEdges = updatedEdges.filter(e => !(e.source === 'start' && e.target === 'end'));
    const afterCount = updatedEdges.filter(e => e.source === 'start' && e.target === 'end').length;
    if (beforeCount > 0 && import.meta.env.DEV) {
      console.log('🗑️ Removed Start->Done auto-complete edge', { beforeCount, afterCount, questionNodesCount: questionNodes.length });
    }
  }
  
  // If start can't reach end, find the path and connect it
  if (!startCanReachEnd) {
    // Find the last question that can be reached from start
    const reachableFromStart = new Set<string>(['start']);
    const dfs = (nodeId: string) => {
      const neighbors = adjacencyList.get(nodeId) || [];
      neighbors.forEach(neighbor => {
        if (!reachableFromStart.has(neighbor)) {
          reachableFromStart.add(neighbor);
          dfs(neighbor);
        }
      });
    };
    dfs('start');
    
    // Find the last reachable question node
    const reachableQuestions = questionNodes
      .filter(n => reachableFromStart.has(n.id))
      .sort((a, b) => {
        // Sort by position (top to bottom)
        return (a.position.y || 0) - (b.position.y || 0);
      });
    
    const lastReachableQuestion = reachableQuestions[reachableQuestions.length - 1];
    
    if (lastReachableQuestion) {
      // Check if ALL options of this question have conditional edges
      // If so, don't create a main path edge - all paths should go through sub-questions
      if (allOptionsAreConditional(lastReachableQuestion.id, nodes, updatedEdges)) {
        if (import.meta.env.DEV) {
          console.log(`⚠️ Skipping main path edge from ${lastReachableQuestion.id} to end: all options are conditional`);
        }
        // Don't create a main path edge - all paths should go through sub-questions
        // The sub-questions should connect to end or the next question
        return updatedEdges;
      }
      
      // Check if there's already an edge from this question to end
      const existingEdgeToEnd = updatedEdges.find(e => 
        e.source === lastReachableQuestion.id && e.target === 'end'
      );
      
      if (!existingEdgeToEnd) {
        // Check if we have a preserved label from the removed edge
        const edgeKey = `${lastReachableQuestion.id}-end`;
        const preservedLabel = removedEdgeLabels?.get(edgeKey);
        
        // If no preserved label, rebuild from question's non-conditional options
        const rebuiltLabel = preservedLabel || rebuildEdgeLabelFromQuestion(lastReachableQuestion.id, nodes, edges);
        
        // Only create edge if there are non-conditional options (rebuiltLabel exists)
        if (rebuiltLabel) {
          const newEdge: Edge = {
            id: `edge-${lastReachableQuestion.id}-end`,
            source: lastReachableQuestion.id,
            target: 'end',
            type: 'smoothstep',
            data: { linear: true },
            reconnectable: true,
            sourceHandle: 'bottom-output',
            targetHandle: 'top-input',
            label: rebuiltLabel,
          };
          
          if (import.meta.env.DEV) {
            if (preservedLabel) {
              console.log(`✅ Preserved label "${preservedLabel}" for edge ${lastReachableQuestion.id} -> end`);
            } else {
              console.log(`✅ Rebuilt label "${rebuiltLabel}" for edge ${lastReachableQuestion.id} -> end from question options`);
            }
          }

          updatedEdges.push(newEdge);
        } else {
          if (import.meta.env.DEV) {
            console.log(`⚠️ No non-conditional options for ${lastReachableQuestion.id}, skipping main path edge to end`);
          }
        }
      }
    } else {
      // No questions reachable, connect start directly to end
      const hasStartToEnd = updatedEdges.some(e => e.source === 'start' && e.target === 'end');
      if (!hasStartToEnd) {
        updatedEdges.push({
          id: 'edge-start-end',
          source: 'start',
          target: 'end',
          type: 'smoothstep',
          data: { linear: true },
          reconnectable: true,
          sourceHandle: 'bottom-output',
          targetHandle: 'top-input',
        });
      }
    }
  }
  
  return updatedEdges;
};

// Check if start can reach end in the flow
export const canStartReachEnd = (nodes: Node[], edges: Edge[]): boolean => {
  const startNode = nodes.find(n => n.id === 'start');
  const endNode = nodes.find(n => n.id === 'end');
  
  if (!startNode || !endNode) {
    return false;
  }
  
  // Build adjacency list
  const adjacencyList = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, []);
    }
    adjacencyList.get(edge.source)!.push(edge.target);
  });
  
  // DFS to check if start can reach end
  const visited = new Set<string>();
  const dfs = (nodeId: string): boolean => {
    if (nodeId === 'end') return true;
    if (visited.has(nodeId)) return false;
    visited.add(nodeId);
    
    const neighbors = adjacencyList.get(nodeId) || [];
    return neighbors.some(neighbor => dfs(neighbor));
  };
  
  return dfs('start');
};

// Check if there's at least 1 question linked from start to done (orphaned questions don't count)
export const hasAtLeastOneQuestionInPath = (nodes: Node[], edges: Edge[]): boolean => {
  const startNode = nodes.find(n => n.id === 'start');
  const endNode = nodes.find(n => n.id === 'end');
  
  if (!startNode || !endNode) {
    return false;
  }
  
  // Build adjacency list
  const adjacencyList = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, []);
    }
    adjacencyList.get(edge.source)!.push(edge.target);
  });
  
  // Find path from start to end using DFS
  const path: string[] = [];
  const visited = new Set<string>();
  
  const dfs = (nodeId: string): boolean => {
    if (nodeId === 'end') {
      path.push(nodeId);
      return true;
    }
    if (visited.has(nodeId)) return false;
    visited.add(nodeId);
    path.push(nodeId);
    
    const neighbors = adjacencyList.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (dfs(neighbor)) {
        return true;
      }
    }
    
    // Backtrack if no path found
    path.pop();
    return false;
  };
  
  dfs('start');
  
  // Count question nodes in the path (excluding start and end, and conditional nodes with decimal IDs)
  const questionCount = path.filter(nodeId => {
    if (nodeId === 'start' || nodeId === 'end') return false;
    const node = nodes.find(n => n.id === nodeId);
    if (!node || node.type !== 'question') return false;
    // Exclude conditional nodes (those with decimal IDs like "2.1")
    if (nodeId.includes('.')) return false;
    return true;
  }).length;
  
  return questionCount >= 1;
};

// Renumber questions in the flow from start to end, preserving question text
// Returns both renumbered nodes and updated edges (edges are updated when conditional node IDs change)
export const renumberQuestionsInFlow = (nodes: Node[], edges: Edge[]): { nodes: Node[]; edges: Edge[] } => {
  if (import.meta.env.DEV) {
    console.log('🔄 renumberQuestionsInFlow called', {
      nodeCount: nodes.length,
      edgeCount: edges.length,
      questionNodes: nodes.filter(n => n.type === 'question' && n.id !== 'start' && n.id !== 'end').length,
      conditionalNodes: nodes.filter(n => n.type === 'question' && n.id.includes('.')).length,
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n'), // Show caller stack
    });
  }

  // Build adjacency list
  const adjacencyList = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!adjacencyList.has(edge.source)) {
      adjacencyList.set(edge.source, []);
    }
    adjacencyList.get(edge.source)!.push(edge.target);
  });
  
  // Build edge map with conditional info
  const edgeMap = new Map<string, Map<string, Edge>>();
  edges.forEach(edge => {
    if (!edgeMap.has(edge.source)) {
      edgeMap.set(edge.source, new Map());
    }
    edgeMap.get(edge.source)!.set(edge.target, edge);
  });
  
  // Get question node IDs (excluding start, end, and sub-questions)
  const questionNodeIds = new Set(
    nodes
      .filter(n => n.id !== 'start' && n.id !== 'end' && n.type === 'question' && !n.id.includes('.'))
      .map(n => n.id)
  );
  
  // Find all questions reachable from start using BFS
  // This traverses ALL branches, including through sub-questions
  const findAllQuestions = (): string[] => {
    const visited = new Set<string>(['start']);
    const questionOrder: string[] = [];
    const queue: string[] = ['start'];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      
      const neighbors = adjacencyList.get(current) || [];
      
      // Process neighbors: prioritize non-conditional edges, but also process conditional branches
      const nonConditionalNeighbors: string[] = [];
      const conditionalNeighbors: string[] = [];
      const subQuestionNeighbors: string[] = [];
      
      neighbors.forEach(targetId => {
        if (targetId === 'end') return; // Skip 'end' during traversal
        if (visited.has(targetId)) return; // Skip already visited
        
        const edge = edgeMap.get(current)?.get(targetId);
        const isConditional = edge ? isConditionalEdge(edge) : false;
        
        if (questionNodeIds.has(targetId)) {
          // Main question node
          if (isConditional) {
            conditionalNeighbors.push(targetId);
          } else {
            nonConditionalNeighbors.push(targetId);
          }
        } else if (targetId.includes('.')) {
          // Sub-question - we need to traverse through it to find the next main question
          subQuestionNeighbors.push(targetId);
        }
      });
      
      // Process non-conditional neighbors first (main path)
      for (const neighbor of nonConditionalNeighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          questionOrder.push(neighbor);
          queue.push(neighbor);
        }
      }
      
      // Process sub-questions - traverse through them to find next main questions
      for (const subQuestionId of subQuestionNeighbors) {
        if (!visited.has(subQuestionId)) {
          visited.add(subQuestionId);
          // Don't add sub-questions to questionOrder (they're numbered separately)
          // But add them to queue to traverse through them
          queue.push(subQuestionId);
        }
      }
      
      // Then process conditional neighbors (branches) - these are main questions reached via conditional edges
      for (const neighbor of conditionalNeighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          questionOrder.push(neighbor);
          queue.push(neighbor);
        }
      }
    }
    
    return questionOrder;
  };
  
  const allQuestions = findAllQuestions();
  if (import.meta.env.DEV) {
    console.log('📋 Found questions to renumber:', {
      questionCount: allQuestions.length,
      questionIds: allQuestions,
    });
  }

  const updatedNodes = [...nodes];
  let questionNumber = 1;
  
  // Process each question in the order found (which respects main path first, then branches)
  for (const nodeId of allQuestions) {
    const nodeIndex = updatedNodes.findIndex(n => n.id === nodeId);
    if (nodeIndex === -1) continue;
    
    const node = updatedNodes[nodeIndex];
    const label = node.data?.label || '';
    
    // Extract question text (everything after "Q{number}:" on first line, plus all other lines)
    const lines = label.split('\n');
    const firstLine = lines[0] || '';
    
    // Extract text after Q{number}: prefix
    const textMatch = firstLine.match(/^Q\d+(?:\.\d+)?:\s*(.+)$/);
    let questionText = '';
    if (textMatch) {
      questionText = textMatch[1].trim();
    } else {
      // If no Q prefix, use the whole first line
      questionText = firstLine.trim();
    }
    
    // If question text matches "Question {number}" pattern, update it to match new Q number
    // This handles cases where text is "Question 4" but should be "Question 1" for Q1
    const questionNumberMatch = questionText.match(/^Question\s+(\d+)$/i);
    if (questionNumberMatch) {
      // Update to match the new question number
      questionText = `Question ${questionNumber}`;
    }
    
    // Get all other lines (options, scale, etc.)
    const otherLines = lines.slice(1);
    
    // Build new label with updated question number (always use sequential number based on position)
    const newLabelParts = [`Q${questionNumber}: ${questionText}`];
    if (otherLines.length > 0) {
      newLabelParts.push(...otherLines);
    }
    const newLabel = newLabelParts.join('\n');
    
    // Update the node - preserve all properties including selected state
    updatedNodes[nodeIndex] = {
      ...node,
      selected: node.selected, // Preserve selection state
      data: {
        ...node.data,
        label: newLabel,
      },
    };
    
    questionNumber++;
  }
  
  // Now renumber conditional questions (those with decimal IDs like "2.1", "2.2")
  // First, update their labels to match their parent's new Q number
  // Then, renumber them sequentially within each parent group
  const conditionalNodes = updatedNodes.filter(n => {
    if (n.id === 'start' || n.id === 'end') return false;
    if (n.type !== 'question') return false;
    return n.id.includes('.');
  });
  
  // Group conditional nodes by their parent's Q number
  // We need to find the parent by matching the Q number in its label, not by ID
  const conditionalNodesByParent = new Map<string, Node[]>();
  
  conditionalNodes.forEach(conditionalNode => {
    const conditionalId = conditionalNode.id;
    const parts = conditionalId.split('.');
    if (parts.length !== 2) return; // Should be like "2.1"
    
    // The first part is the parent's Q number (from when the conditional was created)
    // But we need to find the actual parent node by matching its Q number in the label
    const parentQNumberFromId = parts[0];
    
    // Find the parent node by matching the Q number in its label
    const parentNode = updatedNodes.find(n => {
      if (n.id === 'start' || n.id === 'end') return false;
      if (n.type !== 'question') return false;
      if (n.id.includes('.')) return false; // Not a conditional itself
      
      const labelMatch = (n.data?.label || '').match(/^Q(\d+)(?:\.\d+)?:/);
      return labelMatch && labelMatch[1] === parentQNumberFromId;
    });
    
    if (!parentNode) return;
    
    // Use the parent's current Q number (from label) as the key
    const parentLabelMatch = (parentNode.data?.label || '').match(/^Q(\d+)(?:\.\d+)?:/);
    if (!parentLabelMatch) return;
    
    const parentQNumber = parentLabelMatch[1];
    
    if (!conditionalNodesByParent.has(parentQNumber)) {
      conditionalNodesByParent.set(parentQNumber, []);
    }
    conditionalNodesByParent.get(parentQNumber)!.push(conditionalNode);
  });
  
  // Track ID mappings for conditional nodes that are renamed
  const idMappings = new Map<string, string>();
  
  // For each parent group, renumber conditional questions sequentially
  // The key is already the parent's Q number (from label), so we can use it directly
  conditionalNodesByParent.forEach((conditionalNodesForParent, parentQNumber) => {
    const newParentQNumber = parentQNumber; // Already the correct Q number from the parent's label
    
    // Sort conditional nodes by their current sub-number to maintain order
    conditionalNodesForParent.sort((a, b) => {
      const aParts = a.id.split('.');
      const bParts = b.id.split('.');
      const aSubNum = parseInt(aParts[1] || '0', 10);
      const bSubNum = parseInt(bParts[1] || '0', 10);
      return aSubNum - bSubNum;
    });
    
    // Renumber sequentially (1, 2, 3, ...)
    conditionalNodesForParent.forEach((conditionalNode, index) => {
      const nodeIndex = updatedNodes.findIndex(n => n.id === conditionalNode.id);
      if (nodeIndex === -1) return;
      
      const oldConditionalId = conditionalNode.id;
      const newSubNumber = index + 1;
      const newConditionalId = `${newParentQNumber}.${newSubNumber}`;
      
      // Track the ID change for edge updates
      if (oldConditionalId !== newConditionalId) {
        idMappings.set(oldConditionalId, newConditionalId);
      }
      
      // Update the conditional node's ID and label
      const conditionalLabel = conditionalNode.data?.label || '';
      const newConditionalLabel = conditionalLabel.replace(
        /^Q\d+\.\d+:/,
        `Q${newConditionalId}:`
      );
      
      updatedNodes[nodeIndex] = {
        ...conditionalNode,
        id: newConditionalId,
        selected: conditionalNode.selected, // Preserve selection state
        data: {
          ...conditionalNode.data,
          label: newConditionalLabel,
        },
      };
    });
  });
  
  // Update edges that reference conditional nodes whose IDs changed
  const updatedEdges = edges.map(edge => {
    let newSource = edge.source;
    let newTarget = edge.target;
    let newId = edge.id;
    
    // Check if source was renamed
    if (idMappings.has(edge.source)) {
      newSource = idMappings.get(edge.source)!;
      newId = newId.replace(edge.source, newSource);
    }
    
    // Check if target was renamed
    if (idMappings.has(edge.target)) {
      newTarget = idMappings.get(edge.target)!;
      newId = newId.replace(edge.target, newTarget);
    }
    
    // Only return a new edge object if something changed
    if (newSource !== edge.source || newTarget !== edge.target || newId !== edge.id) {
      return {
        ...edge,
        source: newSource,
        target: newTarget,
        id: newId,
      };
    }
    
    return edge;
  });
  
  const renumberedQuestionLabels = updatedNodes
    .filter(n => n.type === 'question' && n.id !== 'start' && n.id !== 'end')
    .map(n => {
      const labelMatch = (n.data?.label || '').match(/^Q(\d+(?:\.\d+)?):/);
      return labelMatch ? labelMatch[1] : null;
    })
    .filter(Boolean);

  if (import.meta.env.DEV) {
    console.log('✅ renumberQuestionsInFlow completed', {
      totalNodes: updatedNodes.length,
      totalEdges: updatedEdges.length,
      renumberedQuestions: renumberedQuestionLabels.length,
      questionNumbers: renumberedQuestionLabels,
      edgesUpdated: edges.filter((e, i) => {
        const updated = updatedEdges[i];
        return updated && (e.source !== updated.source || e.target !== updated.target || e.id !== updated.id);
      }).length,
    });
  }
  
  return { nodes: updatedNodes, edges: updatedEdges };
};

export const formatQuestionLabel = (question: SurveyQuestionTemplate) => {
  if (question.type === 'done') {
    return 'Done';
  }

  const lines = [`Q${question.number}: ${question.question ?? ''}`.trim()];

  if (question.type === 'rating' && question.type_config) {
    lines.push(
      `Scale: ${question.type_config.format ?? 'numerical'} (${question.type_config.rate_amount ?? 5})`
    );
  }

  if (question.type === 'radio' && question.type_config?.radio_options) {
    lines.push(`Options: ${question.type_config.radio_options.join(', ')}`);
  }

  if (question.type === 'checkbox' && question.type_config?.checkbox_options) {
    lines.push(`Choices: ${question.type_config.checkbox_options.join(', ')}`);
  }

  return lines.join('\n');
};

export const formatConditionalLabel = (config?: SurveyQuestionTemplate['conditional_config']) => {
  if (!config) {
    return '';
  }

  return [`Q${config.number}: ${config.response_question}`, `Type: ${config.type}`]
    .filter(Boolean)
    .join('\n');
};

/** Coerce legacy question number (may be number or string) to number for comparison. */
function toQuestionNumber(value: number | string): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Normalize legacy survey array so the first main question is Q1.
 * Some APIs return questions numbered 2, 3, ... (or string "2", "3"); this renumbers to 1, 2, ... and adjusts conditionals (e.g. 2.1 -> 1.1).
 */
export function normalizeLegacyArrayQuestionNumbers(
  questions: SurveyQuestionTemplate[]
): SurveyQuestionTemplate[] {
  if (questions.length === 0) return questions;
  const mainNumbers = questions
    .map((q) => ({ raw: q.number, num: toQuestionNumber(q.number) }))
    .filter(({ raw }) => !String(raw).includes('.'))
    .map(({ num }) => num)
    .filter((n) => Number.isFinite(n));
  if (mainNumbers.length === 0) return questions;
  const minMain = Math.min(...mainNumbers);
  if (minMain <= 1) return questions;
  const shift = minMain - 1;
  return questions.map((q) => {
    const num = q.number;
    const numStr = String(num);
    const isConditional = numStr.includes('.');
    let newNumber: number;
    if (isConditional) {
      const parts = numStr.split('.');
      const main = Number(parts[0]);
      const sub = parts[1] != null ? Number(parts[1]) : undefined;
      const newMain = main - shift;
      newNumber = sub != null && Number.isFinite(sub) ? parseFloat(`${newMain}.${sub}`) : newMain;
    } else {
      const numVal = toQuestionNumber(num);
      newNumber = numVal - shift;
    }
    const updated: SurveyQuestionTemplate = { ...q, number: newNumber };
    if (q.conditional_config != null) {
      const cn = q.conditional_config.number;
      const cnStr = String(cn);
      const condIsDecimal = cnStr.includes('.');
      let newCondNumber: number;
      if (condIsDecimal) {
        const cParts = cnStr.split('.');
        const cm = Number(cParts[0]);
        const cs = cParts[1] != null ? Number(cParts[1]) : undefined;
        newCondNumber = cs != null && Number.isFinite(cs) ? parseFloat(`${cm - shift}.${cs}`) : cm - shift;
      } else {
        newCondNumber = toQuestionNumber(cn) - shift;
      }
      updated.conditional_config = { ...q.conditional_config, number: newCondNumber };
    }
    return updated;
  });
}

export const generateFlowFromTemplate = (questions: SurveyQuestionTemplate[]) => {
  const normalized = normalizeLegacyArrayQuestionNumbers(questions);
  const nodes: Node[] = [
    {
      id: 'start',
      type: 'input',
      data: { label: 'Start' },
      position: { x: BASE_X, y: 0 },
      style: START_NODE_STYLE,
    },
  ];
  const edges: Edge[] = [];
  let previousNodeId = 'start';

  normalized.forEach((question, index) => {
    // Skip conditional questions (they're added separately when processing their parent)
    if (question.number.toString().includes('.')) {
      return;
    }

    const nodeId = question.type === 'done' ? 'end' : question.number.toString();
    const yPos = (index + 1) * VERTICAL_SPACING;

    nodes.push({
      id: nodeId,
      type: question.type === 'done' ? 'output' : 'question',
      data: { label: formatQuestionLabel(question), meta: question },
      position: { x: BASE_X, y: yPos },
      style: question.type === 'done' ? END_NODE_STYLE : BASE_NODE_STYLE,
    });

    // Always create a regular edge from previous to current (unless it's done)
    if (question.type !== 'done') {
      // Check if edge already exists (might be created by conditional logic)
      const edgeExists = edges.some(e => e.source === previousNodeId && e.target === nodeId);
      if (!edgeExists) {
        edges.push({
          id: `edge-${previousNodeId}-${nodeId}`,
          source: previousNodeId,
          target: nodeId,
          type: 'smoothstep',
          reconnectable: true,
          sourceHandle: previousNodeId === 'start' ? 'bottom-output' : 'bottom-output',
          targetHandle: 'top-input',
        });
      }
    }

    // Handle conditional logic
    if (question.conditional && question.conditional_config) {
      const conditionalNodeId = question.conditional_config.number.toString();
      
      // Add conditional question node
      nodes.push({
        id: conditionalNodeId,
        type: 'question',
        data: { 
          label: formatConditionalLabel(question.conditional_config), 
          meta: question.conditional_config,
          autoGeneratedConditional: true,
        },
        position: { x: CONDITIONAL_RIGHT_X, y: yPos + CONDITIONAL_OFFSET_Y },
        style: CONDITIONAL_NODE_STYLE,
      });

      // Create conditional edge from source to conditional question (non-reconnectable - managed automatically)
      const conditionValue = question.conditional_config.condition;
      edges.push({
        id: `edge-${nodeId}-${conditionalNodeId}`,
        source: nodeId,
        target: conditionalNodeId,
        type: 'smoothstep',
        animated: true,
        label: conditionValue,
        style: { strokeDasharray: '6 4', stroke: '#d32f2f' },
        data: {
          conditionalOption: conditionValue,
        },
        reconnectable: false, // Prevent manual reconnection - managed automatically
        sourceHandle: 'right-output',
        targetHandle: 'left-input',
      });

      // Find the next main question (not conditional) for connecting back to main flow
      const nextMainQuestion = normalized.slice(index + 1).find(q => q.type !== 'done' && !q.number.toString().includes('.'));
      const nextTargetId = nextMainQuestion ? nextMainQuestion.number.toString() : 'end';

      // Connect conditional question back to main flow
      edges.push({
        id: `edge-${conditionalNodeId}-${nextTargetId}`,
        source: conditionalNodeId,
        target: nextTargetId,
        type: 'smoothstep',
        reconnectable: true,
        sourceHandle: 'bottom-output',
        targetHandle: nextTargetId === 'end' ? 'top-input' : 'top-input',
      });
      
      // For non-conditional options, create a regular edge from source to next question
      // This handles cases where some options have conditionals and others don't
      // Collect all options that don't have conditional questions
      const allOptions = question.type_config?.radio_options || question.type_config?.checkbox_options || [];
      const conditionalOption = question.conditional_config?.condition;
      // Split condition value if it contains multiple options (space-separated)
      const conditionalOptions = conditionalOption ? conditionalOption.split(' ').filter(Boolean) : [];
      const nonConditionalOptions = allOptions.filter(opt => !conditionalOptions.includes(opt));
      
      // Only create if there isn't already a regular edge from this node
      const hasRegularEdge = edges.some(e => e.source === nodeId && !e.data?.conditionalOption);
      if (!hasRegularEdge && nonConditionalOptions.length > 0) {
        edges.push({
          id: `edge-${nodeId}-${nextTargetId}`,
          source: nodeId,
          target: nextTargetId,
          type: 'smoothstep',
          reconnectable: true,
          sourceHandle: 'bottom-output',
          targetHandle: nextTargetId === 'end' ? 'top-input' : 'top-input',
          label: nonConditionalOptions.join(', '), // Label with non-conditional options
        });
      } else if (hasRegularEdge && nonConditionalOptions.length > 0) {
        // Update existing regular edge label
        const regularEdgeIndex = edges.findIndex(e => e.source === nodeId && !e.data?.conditionalOption);
        if (regularEdgeIndex >= 0) {
          edges[regularEdgeIndex] = {
            ...edges[regularEdgeIndex],
            label: nonConditionalOptions.join(', '), // Update label with all non-conditional options
          };
        }
      }
    }

    if (question.type !== 'done') {
      previousNodeId = nodeId;
    }
  });

  return { nodes, edges };
};

export const sortSurveysById = (list: Survey[]) => [...list].sort((a, b) => a.id - b.id);
/**
 * Build the stored survey link for the megsapp public host.
 * Production surveys use path-only URLs (e.g. /19/) because reward and customer
 * context are resolved when the respondent opens the survey, not in the saved link.
 * Query params (?reward=…&i_customer=…) were removed so new records match legacy
 * rows and avoid placeholder values being persisted in the database.
 * @param _rewardAmount Kept for existing call sites; not included in the URL.
 */
export const buildSurveyLink = (id: number, _rewardAmount?: string) => {
  return `https://surveys.megsapp.com/${id}/`;
};

// Calculate point along a smoothstep path at a given percentage (0-1)
// Uses getSmoothStepPath to get the actual path and interpolate along it
export const getPointAlongSmoothStepPath = (
  sourceX: number,
  sourceY: number,
  sourcePosition: Position,
  targetX: number,
  targetY: number,
  targetPosition: Position,
  percentage: number
): { x: number; y: number } => {
  // Use getSmoothStepPath to get the actual path and default label position
  const [path, defaultLabelX, defaultLabelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  
  // For now, use linear interpolation between source and target
  // This is an approximation, but works well for most cases
  // The actual smoothstep path has corners, but labels are typically placed
  // along the straight segments, so linear interpolation is sufficient
  const x = sourceX + (targetX - sourceX) * percentage;
  const y = sourceY + (targetY - sourceY) * percentage;
  
  return { x, y };
};

// Calculate optimal label positions for edges to avoid overlaps
export const calculateOptimalLabelPositions = (
  edges: Edge[],
  nodes: Node[]
): Map<string, { x: number; y: number; offset: number }> => {
  const labelPositions = new Map<string, { x: number; y: number; offset: number }>();
  const LABEL_HEIGHT = 20; // Approximate label height
  const LABEL_SPACING = 30; // Minimum spacing between labels
  const CANDIDATE_POSITIONS = [0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75]; // Percentages along edge
  
  // Get node positions map
  const nodePositions = new Map<string, { x: number; y: number }>();
  nodes.forEach(node => {
    nodePositions.set(node.id, node.position);
  });
  
  // Get handle positions and positions (for getSmoothStepPath)
  const getHandlePosition = (
    nodeId: string,
    handleType: string | null | undefined,
    isSource: boolean
  ): { x: number; y: number; position: Position } | null => {
    const nodePos = nodePositions.get(nodeId);
    if (!nodePos) return null;
    
    const NODE_WIDTH = 280;
    const NODE_HEIGHT = 120; // Approximate
    
    // Approximate handle positions based on handle type
    let x: number, y: number, position: Position;
    
    if (handleType === 'top-input') {
      x = nodePos.x + NODE_WIDTH / 2;
      y = nodePos.y;
      position = Position.Top;
    } else if (handleType === 'bottom-output') {
      x = nodePos.x + NODE_WIDTH / 2;
      y = nodePos.y + NODE_HEIGHT;
      position = Position.Bottom;
    } else if (handleType === 'left-input' || handleType === 'left-output') {
      x = nodePos.x;
      y = nodePos.y + NODE_HEIGHT / 2;
      position = Position.Left;
    } else if (handleType === 'right-input' || handleType === 'right-output') {
      x = nodePos.x + NODE_WIDTH;
      y = nodePos.y + NODE_HEIGHT / 2;
      position = Position.Right;
    } else {
      // Default: center of node
      x = nodePos.x + NODE_WIDTH / 2;
      y = nodePos.y + NODE_HEIGHT / 2;
      position = Position.Top;
    }
    
    return { x, y, position };
  };
  
  // Process edges with labels
  const edgesWithLabels = edges.filter(e => e.label);
  
  edgesWithLabels.forEach((edge, index) => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) return;
    
    const sourceHandleInfo = getHandlePosition(edge.source, edge.sourceHandle, true);
    const targetHandleInfo = getHandlePosition(edge.target, edge.targetHandle, false);
    
    if (!sourceHandleInfo || !targetHandleInfo) return;
    
    // Use getSmoothStepPath to get the actual default label position
    const [path, defaultLabelX, defaultLabelY] = getSmoothStepPath({
      sourceX: sourceHandleInfo.x,
      sourceY: sourceHandleInfo.y,
      sourcePosition: sourceHandleInfo.position,
      targetX: targetHandleInfo.x,
      targetY: targetHandleInfo.y,
      targetPosition: targetHandleInfo.position,
    });
    
    // Try candidate positions and find one that doesn't overlap
    let bestPosition: { x: number; y: number; offset: number } | null = null;
    
    for (const percentage of CANDIDATE_POSITIONS) {
      // Use getSmoothStepPath to calculate position at this percentage
      // We'll approximate by using the path and interpolating
      const candidatePos = getPointAlongSmoothStepPath(
        sourceHandleInfo.x,
        sourceHandleInfo.y,
        sourceHandleInfo.position,
        targetHandleInfo.x,
        targetHandleInfo.y,
        targetHandleInfo.position,
        percentage
      );
      
      // Check for overlaps with existing label positions
      let hasOverlap = false;
      
      for (const [existingEdgeId, existingPos] of labelPositions.entries()) {
        if (existingEdgeId === edge.id) continue;
        
        const distance = Math.sqrt(
          Math.pow(candidatePos.x - existingPos.x, 2) +
          Math.pow(candidatePos.y - existingPos.y, 2)
        );
        
        if (distance < LABEL_SPACING) {
          hasOverlap = true;
          break;
        }
      }
      
      if (!hasOverlap) {
        // Also check for overlaps with other edges' paths
        let pathOverlap = false;
        for (const otherEdge of edgesWithLabels) {
          if (otherEdge.id === edge.id) continue;
          
          const otherSourceNode = nodes.find(n => n.id === otherEdge.source);
          const otherTargetNode = nodes.find(n => n.id === otherEdge.target);
          if (!otherSourceNode || !otherTargetNode) continue;
          
          const otherSourceHandleInfo = getHandlePosition(otherEdge.source, otherEdge.sourceHandle, true);
          const otherTargetHandleInfo = getHandlePosition(otherEdge.target, otherEdge.targetHandle, false);
          if (!otherSourceHandleInfo || !otherTargetHandleInfo) continue;
          
          // Check distance to other edge's path
          const distToOtherPath = pointToLineSegmentDistance(
            candidatePos.x,
            candidatePos.y,
            otherSourceHandleInfo.x,
            otherSourceHandleInfo.y,
            otherTargetHandleInfo.x,
            otherTargetHandleInfo.y
          );
          
          if (distToOtherPath < LABEL_SPACING / 2) {
            pathOverlap = true;
            break;
          }
        }
        
        if (!pathOverlap) {
          bestPosition = {
            x: candidatePos.x,
            y: candidatePos.y,
            offset: percentage,
          };
          break;
        }
      }
    }
    
    // If no non-overlapping position found, use default from getSmoothStepPath
    if (!bestPosition) {
      bestPosition = {
        x: defaultLabelX,
        y: defaultLabelY,
        offset: 0.5,
      };
    }
    
    labelPositions.set(edge.id, bestPosition);
  });
  
  return labelPositions;
};

